import React, { useState } from 'react';
import { MonthlyOtData } from '../interface/ot.interface';

interface StackedBarChartProps {
  data: MonthlyOtData[];
}

interface BarSegment {
  status: 'pendientes' | 'enProceso' | 'completadas' | 'canceladas';
  label: string;
  color: string;
  percentage: number;
  value: number;
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({ data }) => {
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  if (!data || data.length === 0) {
    return <div style={{ color: '#94a3b8' }}>No hay datos disponibles</div>;
  }

  const statusConfig = {
    pendientes: { label: 'Pendientes', color: '#f59e0b', icon: '⏳' },
    enProceso: { label: 'En Proceso', color: '#3b82f6', icon: '🔄' },
    completadas: { label: 'Completadas', color: '#10b981', icon: '✅' },
    canceladas: { label: 'Canceladas', color: '#ef4444', icon: '❌' }
  };

  const getBarSegments = (item: MonthlyOtData): any[] => {
    const total = item.total || 1;
    return [
      {
        status: 'completadas',
        label: statusConfig.completadas.label,
        color: statusConfig.completadas.color,
        percentage: (item.completadas / total) * 100,
        value: item.completadas
      },
      {
        status: 'enProceso',
        label: statusConfig.enProceso.label,
        color: statusConfig.enProceso.color,
        percentage: (item.enProceso / total) * 100,
        value: item.enProceso
      },
      {
        status: 'pendientes',
        label: statusConfig.pendientes.label,
        color: statusConfig.pendientes.color,
        percentage: (item.pendientes / total) * 100,
        value: item.pendientes
      },
      {
        status: 'canceladas',
        label: statusConfig.canceladas.label,
        color: statusConfig.canceladas.color,
        percentage: (item.canceladas / total) * 100,
        value: item.canceladas
      }
    ].filter(segment => segment.value > 0);
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Gráfico */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '12px', padding: '20px' }}>
        {data.map((item, index) => {
          const segments = getBarSegments(item);
          const isHovered = hoveredMonth === index;

          return (
            <div
              key={index}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                minWidth: '60px'
              }}
            >
              {/* Valor total */}
              <div style={{
                fontSize: '0.85rem',
                fontWeight: '700',
                color: isHovered ? '#1e293b' : '#64748b',
                transition: 'all 0.2s'
              }}>
                {item.total}
              </div>

              {/* Barra apilada */}
              <div
                style={{
                  width: '100%',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={() => setHoveredMonth(index)}
                onMouseLeave={() => setHoveredMonth(null)}
              >
                {segments.map((segment) => {
                  const segmentKey = `${index}-${segment.status}`;
                  const isSegmentHovered = hoveredSegment === segmentKey;

                  return (
                    <div
                      key={segment.status}
                      style={{
                        height: `${segment.percentage}%`,
                        background: segment.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        opacity: isSegmentHovered ? 0.9 : 1,
                        position: 'relative'
                      }}
                      onMouseEnter={() => setHoveredSegment(segmentKey)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      title={`${segment.label}: ${segment.value} (${segment.percentage.toFixed(1)}%)`}
                    >
                      {segment.percentage > 15 && (
                        <span>{segment.value}</span>
                      )}

                      {/* Tooltip en hover */}
                      {isSegmentHovered && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '110%',
                          transform: 'translateY(-50%)',
                          background: '#1e293b',
                          color: 'white',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                          boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                        }}>
                          {segment.label}: {segment.value}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Etiqueta del mes */}
              <div style={{
                fontSize: '0.8rem',
                fontWeight: isHovered ? '600' : '500',
                color: isHovered ? '#1e293b' : '#64748b',
                transition: 'all 0.2s'
              }}>
                {item.month}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        marginTop: '20px',
        padding: '16px',
        borderTop: '1px solid #e2e8f0'
      }}>
        {Object.entries(statusConfig).map(([key, config]) => (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem'
            }}
          >
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '3px',
              background: config.color
            }}></div>
            <span style={{ color: '#64748b' }}>
              {config.icon} {config.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StackedBarChart;